import type { AuthModalMode } from './modalAdapter.js'
import type { AuthLoginStep, ModalFlowKind, SubscriptionControlStep, SyncActivationStep } from './modalFlow.js'

export type ModalDescriptorFlow = ModalFlowKind | 'account-delete' | 'local-protection'
export type AccountDeleteStep = 'export' | 'confirm'
export type LocalProtectionStep =
  | 'local-found'
  | 'anonymous-login-gate'
  | 'sync-exit-unsafe'
  | 'deactivated-local-found'
  | 'protect-local-space-open-file'
  | 'protect-current-space-open-file'
  | 'local-memory-drive-recovery'
export type ModalDescriptorInput =
  | { kind: 'none' }
  | {
    kind: 'email' | 'otp' | 'username' | 'confirmation'
    inputMode: 'email' | 'numeric' | 'text'
    autocomplete: string
    placeholder: string
    maxLength?: number
  }
export type ModalDescriptorCloseRule = 'allowed' | 'blocked'
export type ModalDescriptorLayout = 'default' | 'stack' | 'full'
export type ModalDescriptorBody =
  | { kind: 'copy' }
  | { kind: 'otp-email' }
  | { kind: 'rich-copy', parts: ModalDescriptorBodyPart[] }
  | { kind: 'memory-list', before: string, items: string[], after: string }
export type ModalDescriptorBodyPart =
  | { kind: 'text', text: string }
  | { kind: 'cloud-value', text: string }
export type ModalDescriptorCustom = 'none' | 'turnstile'
export type ModalDescriptorActionEvent =
  | 'AUTH_EMAIL_SUBMIT'
  | 'AUTH_HUMAN_CHECK_RETRY'
  | 'AUTH_OTP_VERIFY'
  | 'AUTH_CHANGE_EMAIL'
  | 'AUTH_OTP_RESEND'
  | 'AUTH_USERNAME_SUBMIT'
  | 'SUBSCRIPTION_CANCEL_CONFIRM'
  | 'SUBSCRIPTION_REACTIVATE_CONFIRM'
  | 'SYNC_ACTIVATION_OPTIONS'
  | 'SYNC_CHECKOUT_MONTHLY'
  | 'SYNC_CHECKOUT_YEARLY'
  | 'SYNC_POST_CHECKOUT_FRESH_LOGIN'
  | 'SYNC_POST_CHECKOUT_CONTINUE'
  | 'SYNC_ACTIVATION_RETRY'
  | 'SYNC_REACTIVATE_FROM_DEACTIVATED'
  | 'ACCOUNT_DELETE_EXPORT'
  | 'ACCOUNT_DELETE_CONFIRM'
  | 'LOCAL_FOUND_SYNC'
  | 'LOCAL_FOUND_DELETE'
  | 'ANONYMOUS_LOGIN_IMPORT'
  | 'ANONYMOUS_LOGIN_DELETE'
  | 'DEACTIVATED_LOCAL_EXPORT_AND_CONTINUE'
  | 'DEACTIVATED_LOCAL_DISCARD_AND_CONTINUE'
  | 'OPEN_FILE_REPLACE'
  | 'SYNC_EXIT_STAY'
  | 'SYNC_EXIT_WIPE'
  | 'LOCAL_MEMORY_RESCUE_EXPORT'
  | 'CONTACT_SUPPORT'

export type ModalDescriptorAction = {
  label: string
  event: ModalDescriptorActionEvent
  placement?: 'primary' | 'secondary' | 'inline' | 'menu'
  tone?: 'sync' | 'danger'
}

export type ModalStepDescriptor = {
  flow: ModalDescriptorFlow
  step: string
  mode: AuthModalMode
  title: string
  copy: string
  actions: ModalDescriptorAction[]
  input: ModalDescriptorInput
  body: ModalDescriptorBody
  custom: ModalDescriptorCustom
  close: ModalDescriptorCloseRule
  layout: ModalDescriptorLayout
  isWaiting?: boolean
}

export type SubscriptionControlModalDescriptorContext = {
  periodEndLabel?: string
}

export type SyncActivationModalDescriptorContext = {
  checkoutEmail?: string
}

export type AuthLoginModalDescriptorContext = {
  checkingHumanFailureReason?: 'failed' | 'unavailable' | 'expired' | 'timeout' | 'otp'
}

export type AccountDeleteModalDescriptorContext = {
  exportReady: boolean
  confirmKeyword: string
}

export type LocalProtectionModalDescriptorContext = {
  anonymousSpaceLabels?: string[]
  unsafeSpaceLabels?: string[]
}

const NO_INPUT: ModalDescriptorInput = { kind: 'none' }
const COPY_BODY: ModalDescriptorBody = { kind: 'copy' }
const NO_CUSTOM: ModalDescriptorCustom = 'none'

const textPart = (text: string): ModalDescriptorBodyPart => ({ kind: 'text', text })
const cloudValuePart = (text: string): ModalDescriptorBodyPart => ({ kind: 'cloud-value', text })

export function selectAuthLoginModalDescriptor(
  step: Exclude<AuthLoginStep, 'done'>,
  context: AuthLoginModalDescriptorContext = {}
): ModalStepDescriptor {
  if (step === 'email') {
    return {
      flow: 'auth-login',
      step,
      mode: 'email',
      title: 'user > login',
      copy: 'Enter your email to receive a one-time code.',
      actions: [{ label: 'send code', event: 'AUTH_EMAIL_SUBMIT', placement: 'primary' }],
      input: {
        kind: 'email',
        inputMode: 'email',
        autocomplete: 'off',
        placeholder: 'mail@domain.com'
      },
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full'
    }
  }

  if (step === 'checking-human') {
    return {
      flow: 'auth-login',
      step,
      mode: 'checking-human',
      title: 'user > checking-human',
      copy: 'Human check before sending your code.',
      actions: [],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: 'turnstile',
      close: 'allowed',
      layout: 'full',
      isWaiting: true
    }
  }

  if (step === 'checking-human-failed') {
    const reason = context.checkingHumanFailureReason ?? 'failed'
    const copy =
      reason === 'unavailable'
        ? 'Human check unavailable right now.\nTry again.'
        : reason === 'expired'
          ? 'Human check expired.\nTry again.'
          : reason === 'timeout'
            ? 'Human check timed out.\nTry again.'
            : reason === 'otp'
              ? 'Code send failed after human check.\nTry again.'
              : 'Human check failed.\nTry again.'
    return {
      flow: 'auth-login',
      step,
      mode: 'checking-human-failed',
      title: 'user > human-check-failed',
      copy,
      actions: [{ label: 'retry', event: 'AUTH_HUMAN_CHECK_RETRY', placement: 'primary' }],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full'
    }
  }

  if (step === 'email-sent') {
    return {
      flow: 'auth-login',
      step,
      mode: 'email-sent',
      title: 'user > enter-code',
      copy: 'Enter the one-time code sent to',
      actions: [
        { label: 'verify', event: 'AUTH_OTP_VERIFY', placement: 'primary' },
        { label: 'change email', event: 'AUTH_CHANGE_EMAIL', placement: 'inline' },
        { label: 'resend code', event: 'AUTH_OTP_RESEND', placement: 'inline' }
      ],
      input: {
        kind: 'otp',
        inputMode: 'numeric',
        autocomplete: 'one-time-code',
        placeholder: '0000 0000',
        maxLength: 8
      },
      body: { kind: 'otp-email' },
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full'
    }
  }

  return selectUsernameModalDescriptor('auth-login')
}

export function selectUsernameModalDescriptor(flow: Extract<ModalFlowKind, 'auth-login' | 'sync-activation'>): ModalStepDescriptor {
  return {
    flow,
    step: 'username',
    mode: 'username',
    title: 'user > username',
    copy: 'Choose your public name.\nWe never expose your login email.\nUse 4-16 characters: letters, numbers, and dashes.',
    actions: [{ label: 'save', event: 'AUTH_USERNAME_SUBMIT', placement: 'primary' }],
    input: {
      kind: 'username',
      inputMode: 'text',
      autocomplete: 'username',
      placeholder: 'public username'
    },
    body: COPY_BODY,
    custom: NO_CUSTOM,
    close: 'blocked',
    layout: 'default'
  }
}

export function selectAccountDeleteModalDescriptor(
  step: AccountDeleteStep,
  context: AccountDeleteModalDescriptorContext
): ModalStepDescriptor {
  if (step === 'export') {
    return {
      flow: 'account-delete',
      step,
      mode: 'account-delete-export',
      title: 'account > delete-account',
      copy: 'Deleting your account permanently removes your Sync data.\nExport your Sync shelf first.',
      actions: [{
        label: context.exportReady ? 'continue to delete' : 'export Sync shelf',
        event: 'ACCOUNT_DELETE_EXPORT',
        placement: 'primary'
      }],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  return {
    flow: 'account-delete',
    step,
    mode: 'account-delete-confirm',
    title: 'account > delete-account',
    copy: `Type ${context.confirmKeyword} to permanently remove your Account data.`,
    actions: [{ label: 'delete account', event: 'ACCOUNT_DELETE_CONFIRM', placement: 'primary' }],
    input: {
      kind: 'confirmation',
      inputMode: 'text',
      autocomplete: 'off',
      placeholder: `type ${context.confirmKeyword}`
    },
    body: COPY_BODY,
    custom: NO_CUSTOM,
    close: 'allowed',
    layout: 'stack'
  }
}

export function selectSyncExitUnsafeModalDescriptor(context: LocalProtectionModalDescriptorContext = {}): ModalStepDescriptor {
  const labels = context.unsafeSpaceLabels?.length
    ? context.unsafeSpaceLabels
    : ['Account Memory']
  return {
    flow: 'local-protection',
    step: 'sync-exit-unsafe',
    mode: 'sync-exit-unsafe',
    title: 'user > logout-warning',
    copy: `Warning! Non-synced edits detected in memory for Space(s):\n\n${labels.join('\n')}\n\nChoose before logout continues:\nStay logged in and wait for network connection to sync to Account.\nWipe and logout to remove Account Memory from this device (full privacy).`,
    actions: [
      { label: 'Stay logged in', event: 'SYNC_EXIT_STAY', placement: 'primary', tone: 'sync' },
      { label: 'Wipe and logout', event: 'SYNC_EXIT_WIPE', placement: 'secondary', tone: 'danger' }
    ],
    input: NO_INPUT,
    body: {
      kind: 'memory-list',
      before: 'Warning! Non-synced edits detected in memory for Space(s):',
      items: labels,
      after: 'Choose before logout continues:\nStay logged in and wait for network connection to sync to Account.\nWipe and logout to remove Account Memory from this device (full privacy).'
    },
    custom: NO_CUSTOM,
    close: 'allowed',
    layout: 'stack'
  }
}

export function selectLocalProtectionModalDescriptor(
  step: LocalProtectionStep,
  context: LocalProtectionModalDescriptorContext = {}
): ModalStepDescriptor {
  if (step === 'sync-exit-unsafe') {
    return selectSyncExitUnsafeModalDescriptor(context)
  }

  if (step === 'local-memory-drive-recovery') {
    return {
      flow: 'local-protection',
      step,
      mode: 'local-memory-drive-recovery',
      title: 'Local Memory drive not accessible',
      copy: "Nothing was changed. Download a rescue copy, then clear this site's data in your browser and reopen Lilo Write.",
      actions: [{
        label: 'download rescue copy',
        event: 'LOCAL_MEMORY_RESCUE_EXPORT',
        placement: 'primary'
      }],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'blocked',
      layout: 'stack'
    }
  }

  if (step === 'local-found') {
    return {
      flow: 'local-protection',
      step,
      mode: 'local-found',
      title: 'space > local-space',
      copy: 'This local space is not in Sync yet.\nKeep it by adding it to Sync, or discard it.',
      actions: [
        { label: 'add space to Sync', event: 'LOCAL_FOUND_SYNC', placement: 'primary' },
        { label: 'discard space', event: 'LOCAL_FOUND_DELETE', placement: 'secondary' }
      ],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'blocked',
      layout: 'stack'
    }
  }

  if (step === 'anonymous-login-gate') {
    const labels = context.anonymousSpaceLabels?.length
      ? context.anonymousSpaceLabels
      : ['Local Anonymous Memory']
    return {
      flow: 'local-protection',
      step,
      mode: 'anonymous-login-gate',
      title: 'login-warning!',
      copy: `Login overwrites Local Memory with Account Memory.\nLocal Memory Space(s) on this device:\n${labels.join('\n')}\nChoose (or close to interrupt login and stay local):\nImport moves Local Spaces into Account Spaces.\nDelete wipes Local Spaces and replaces them with Account Spaces.`,
      actions: [
        { label: 'Import Local into Account', event: 'ANONYMOUS_LOGIN_IMPORT', placement: 'primary', tone: 'sync' },
        { label: 'Delete Local and Replace', event: 'ANONYMOUS_LOGIN_DELETE', placement: 'secondary', tone: 'danger' }
      ],
      input: NO_INPUT,
      body: {
        kind: 'memory-list',
        before: 'Login overwrites Local Memory with Account Memory.\nLocal Memory Space(s) on this device:',
        items: labels,
        after: 'Choose (or close to interrupt login and stay local):\nImport moves Local Spaces into Account Spaces.\nDelete wipes Local Spaces and replaces them with Account Spaces.'
      },
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  if (step === 'deactivated-local-found') {
    const labels = context.anonymousSpaceLabels?.length
      ? context.anonymousSpaceLabels
      : ['Local Memory']
    return {
      flow: 'local-protection',
      step,
      mode: 'deactivated-local-found',
      title: 'login > protect-local-memory',
      copy: `You are opening a deactivated Account.\nLocal Memory on this device cannot be added to Account Memory.\nLocal Memory Space(s) on this device:\n${labels.join('\n')}\nExport Local Memory before continuing, or discard it and open Account.`,
      actions: [
        { label: 'export Local Memory and continue', event: 'DEACTIVATED_LOCAL_EXPORT_AND_CONTINUE', placement: 'primary' },
        { label: 'discard Local Memory and continue', event: 'DEACTIVATED_LOCAL_DISCARD_AND_CONTINUE', placement: 'secondary', tone: 'danger' }
      ],
      input: NO_INPUT,
      body: {
        kind: 'memory-list',
        before: 'You are opening a deactivated Account.\nLocal Memory on this device cannot be added to Account Memory.\nLocal Memory Space(s) on this device:',
        items: labels,
        after: 'Export Local Memory before continuing, or discard it and open Account.'
      },
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  if (step === 'protect-local-space-open-file') {
    return {
      flow: 'local-protection',
      step,
      mode: 'protect-local-space-open-file',
      title: 'space > protect-local-space',
      copy: 'Opening a file replaces the current space on this device.\nUse `space-export-file` to secure the current space first.\nUse `space-import-file` to merge file content instead of replacing it.',
      actions: [{ label: 'open and replace', event: 'OPEN_FILE_REPLACE', placement: 'primary' }],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full'
    }
  }

  return {
    flow: 'local-protection',
    step,
    mode: 'protect-current-space-open-file',
    title: 'space > protect-current-space',
    copy: 'Opening a file replaces the current space on this device.\nYou have Sync active, but this device seems offline, so uncommitted changes will be lost.\nUse `space-import-file` to merge file content instead of replacing it.',
    actions: [{ label: 'open and replace', event: 'OPEN_FILE_REPLACE', placement: 'primary' }],
    input: NO_INPUT,
    body: COPY_BODY,
    custom: NO_CUSTOM,
    close: 'allowed',
    layout: 'full'
  }
}

export function selectSubscriptionControlModalDescriptor(
  step: SubscriptionControlStep,
  context: SubscriptionControlModalDescriptorContext = {}
): ModalStepDescriptor {
  const periodEndLabel = context.periodEndLabel?.trim()

  if (step === 'cancel-confirm') {
    return {
      flow: 'subscription-control',
      step,
      mode: 'sync-cancel-subscription',
      title: 'sync > cancel-subscription',
      copy: periodEndLabel
        ? `This cancels your Account sync subscription.\nAccount sync stays active until ${periodEndLabel}.\nYou can reactivate before then.`
        : 'This cancels your Account sync subscription.\nAccount sync stays active until the end of the current billing period.\nYou can reactivate before then.',
      actions: [{ label: 'cancel Sync subscription', event: 'SUBSCRIPTION_CANCEL_CONFIRM', placement: 'primary' }],
      input: NO_INPUT,
      body: periodEndLabel
        ? {
          kind: 'rich-copy',
          parts: [
            textPart('This cancels your Account sync subscription.\nAccount sync stays active until '),
            cloudValuePart(periodEndLabel),
            textPart('.\nYou can reactivate before then.')
          ]
        }
        : COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  if (step === 'cancel-success') {
    return {
      flow: 'subscription-control',
      step,
      mode: 'sync-cancelled',
      title: 'sync > subscription-cancelled',
      copy: periodEndLabel
        ? `Your Account sync subscription is cancelled.\nAccount sync stays active until ${periodEndLabel}.`
        : 'Your Account sync subscription is cancelled.\nAccount sync stays active until the end of the current billing period.',
      actions: [],
      input: NO_INPUT,
      body: periodEndLabel
        ? {
          kind: 'rich-copy',
          parts: [
            textPart('Your Account sync subscription is cancelled.\nAccount sync stays active until '),
            cloudValuePart(periodEndLabel),
            textPart('.')
          ]
        }
        : COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full'
    }
  }

  if (step === 'reactivate-confirm') {
    return {
      flow: 'subscription-control',
      step,
      mode: 'sync-reactivate-subscription',
      title: 'sync > reactivate-subscription',
      copy: periodEndLabel
        ? `Your Account sync subscription ends on ${periodEndLabel}.\nReactivate before then to keep Account sync active without interruption.`
        : 'Your Account sync subscription is scheduled to end.\nReactivate before then to keep Account sync active without interruption.',
      actions: [{ label: 'reactivate Sync subscription', event: 'SUBSCRIPTION_REACTIVATE_CONFIRM', placement: 'primary' }],
      input: NO_INPUT,
      body: periodEndLabel
        ? {
          kind: 'rich-copy',
          parts: [
            textPart('Your Account sync subscription ends on '),
            cloudValuePart(periodEndLabel),
            textPart('.\nReactivate before then to keep Account sync active without interruption.')
          ]
        }
        : COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  return {
    flow: 'subscription-control',
    step,
    mode: 'sync-reactivated',
    title: 'sync > subscription-reactivated',
    copy: 'Your Account sync subscription is active again.\nAccount sync continues without interruption.',
    actions: [],
    input: NO_INPUT,
    body: COPY_BODY,
    custom: NO_CUSTOM,
    close: 'allowed',
    layout: 'full'
  }
}

export function selectSyncActivationModalDescriptor(
  step: Exclude<SyncActivationStep, 'username' | 'done'>,
  context: SyncActivationModalDescriptorContext = {}
): ModalStepDescriptor {
  const checkoutEmail = context.checkoutEmail?.trim()

  if (step === 'threshold') {
    return {
      flow: 'sync-activation',
      step,
      mode: 'sync-subscription-threshold',
      title: 'sync > no-active-account',
      copy: checkoutEmail
        ? `${checkoutEmail} access verified, but no active account was found.\nLilo Write local memory is free for everyone.\nA paid account adds:\n> Account sync across any device you log in to\n> backup so your spaces are not lost\nsupport for independent, secure development`
        : 'Access verified, but no active account was found.\nLilo Write local memory is free for everyone.\nA paid account adds:\n> Account sync across any device you log in to\n> backup so your spaces are not lost\nsupport for independent, secure development',
      actions: [{ label: 'check sync options', event: 'SYNC_ACTIVATION_OPTIONS', placement: 'primary' }],
      input: NO_INPUT,
      body: checkoutEmail
        ? {
          kind: 'rich-copy',
          parts: [
            textPart(''),
            cloudValuePart(checkoutEmail),
            textPart(' access verified, but no active account was found.\nLilo Write local memory is free for everyone.\nA paid account adds:\n> Account sync across any device you log in to\n> backup so your spaces are not lost\nsupport for independent, secure development')
          ]
        }
        : COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  if (step === 'options') {
    return {
      flow: 'sync-activation',
      step,
      mode: 'checkout-start',
      title: 'sync > subscription-options',
      copy: 'Choose a Sync subscription.\nSecure Stripe checkout, none of your data is collected.\nBilling starts on the 1st of next month; the rest of this month is on us.\nCancel anytime.',
      actions: [
        { label: 'Sync : €4/month', event: 'SYNC_CHECKOUT_MONTHLY', placement: 'menu' },
        { label: 'Sync : €24/year [€2/month]', event: 'SYNC_CHECKOUT_YEARLY', placement: 'menu' }
      ],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  if (step === 'activating') {
    return {
      flow: 'sync-activation',
      step,
      mode: 'checkout-activating',
      title: 'sync > activating',
      copy: 'Your account for sync and backup is being activated.',
      actions: [],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full',
      isWaiting: true
    }
  }

  if (step === 'login-ready') {
    return {
      flow: 'sync-activation',
      step,
      mode: 'checkout-landing',
      title: 'sync > subscription-active',
      copy: checkoutEmail
        ? `Account sync and backup are on for ${checkoutEmail}.\nPick a username at the next login; we never share your email.\nThis email is all you need to log in, no password to remember.\nYou get disposable access codes there each time.`
        : 'Account sync and backup are active.\nYour spaces can mirror across your devices.',
      actions: [
        {
          label: checkoutEmail ? 'send code' : 'continue',
          event: checkoutEmail ? 'SYNC_POST_CHECKOUT_FRESH_LOGIN' : 'SYNC_POST_CHECKOUT_CONTINUE',
          placement: 'primary'
        }
      ],
      input: NO_INPUT,
      body: checkoutEmail
        ? {
          kind: 'rich-copy',
          parts: [
            textPart('Account sync and backup are on for '),
            cloudValuePart(checkoutEmail),
            textPart('.\nPick a username at the next login; we never share your email.\nThis email is all you need to log in, no password to remember.\nYou get disposable access codes there each time.')
          ]
        }
        : COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full'
    }
  }

  if (step === 'landing') {
    return {
      flow: 'sync-activation',
      step,
      mode: 'checkout-landing',
      title: 'sync > subscription-active',
      copy: 'Account sync and backup are active.\nYour spaces can mirror across your devices.',
      actions: [{ label: 'continue', event: 'SYNC_POST_CHECKOUT_CONTINUE', placement: 'primary' }],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'full'
    }
  }

  if (step === 'timeout') {
    return {
      flow: 'sync-activation',
      step,
      mode: 'checkout-timeout',
      title: 'sync > activation-pending',
      copy: 'Sync is not active yet.\nThis can take a moment after checkout.',
      actions: [
        { label: 'check again', event: 'SYNC_ACTIVATION_RETRY', placement: 'primary' },
        { label: 'contact support', event: 'CONTACT_SUPPORT', placement: 'secondary' }
      ],
      input: NO_INPUT,
      body: COPY_BODY,
      custom: NO_CUSTOM,
      close: 'allowed',
      layout: 'stack'
    }
  }

  return {
    flow: 'sync-activation',
    step,
    mode: 'sync-reactivate-from-deactivated',
    title: 'sync > reactivate-subscription',
    copy: 'Your Account sync subscription is inactive.\nTo restore Account sync, start a new subscription.',
    actions: [{ label: 'continue to Sync options', event: 'SYNC_REACTIVATE_FROM_DEACTIVATED', placement: 'primary' }],
    input: NO_INPUT,
    body: COPY_BODY,
    custom: NO_CUSTOM,
    close: 'allowed',
    layout: 'stack'
  }
}

export function authLoginModalModeToDescriptorStep(
  mode: AuthModalMode | undefined
): Exclude<AuthLoginStep, 'done'> | undefined {
  if (mode === 'email') return 'email'
  if (mode === 'checking-human') return 'checking-human'
  if (mode === 'checking-human-failed') return 'checking-human-failed'
  if (mode === 'email-sent') return 'email-sent'
  if (mode === 'username') return 'username'
  return undefined
}

export function subscriptionControlModalModeToStep(
  mode: AuthModalMode | undefined
): SubscriptionControlStep | undefined {
  if (mode === 'sync-cancel-subscription') return 'cancel-confirm'
  if (mode === 'sync-cancelled') return 'cancel-success'
  if (mode === 'sync-reactivate-subscription') return 'reactivate-confirm'
  if (mode === 'sync-reactivated') return 'reactivate-success'
  return undefined
}

export function syncActivationModalModeToDescriptorStep(
  mode: AuthModalMode | undefined
): Exclude<SyncActivationStep, 'username' | 'done'> | undefined {
  if (mode === 'sync-subscription-threshold') return 'threshold'
  if (mode === 'checkout-start') return 'options'
  if (mode === 'checkout-activating') return 'activating'
  if (mode === 'checkout-landing') return 'landing'
  if (mode === 'checkout-timeout') return 'timeout'
  if (mode === 'sync-reactivate-from-deactivated') return 'reactivate-from-deactivated'
  return undefined
}
